// GENERAL RENEWALS (schema 8) — the general-insurance renewal pipeline inside the Renewals view: requested quotes →
// compared → sent to client → approved → renewed (or lost). Every move is a domain command and a line on the client's
// timeline; follow-ups at 45/30/15 days before expiry (Settings) are created by the runner device once a day, or here.
import { useMemo, useState } from 'react';
import {
  GI_RENEWAL_STAGES, GI_OPEN_STAGES, generalRenewalPipeline, moveGeneralRenewal, createGeneralRenewalTasks, planGeneralRenewalTasks, giRenewalDays,
  type GiRenewalItem, type GiRenewalStage, type GiTaskPlan,
} from '@vero/domain';
import { useRuntime } from '../core/runtime.tsx';
import { Empty, Field, Modal, NumInput, PersonCell } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';

type Ev = { target: HTMLInputElement };
type SelEv = { target: HTMLSelectElement };
const COLUMNS = ['new', ...GI_OPEN_STAGES] as const;
type Column = typeof COLUMNS[number];

/** translated follow-up title (used by the board and the daily run) */
export function giTaskTitle(t: (k: string, p?: Record<string, string | number>) => string) {
  return (pl: GiTaskPlan) => t('gi_renewals.task_title', { days: pl.days, product: pl.policy.productName });
}

export function GeneralRenewalsBoard() {
  const rt = useRuntime();
  const { t, i18n, view, today } = rt;
  const items = useMemo(() => generalRenewalPipeline(view, today), [view, today]);
  const plans = useMemo(() => planGeneralRenewalTasks(view, today), [view, today]);
  const col = (it: GiRenewalItem): Column => (it.stage && it.stage !== 'renewed' && it.stage !== 'lost' ? it.stage : 'new');
  const createTasks = () => void rt.run((v, c) => createGeneralRenewalTasks(v, c, giTaskTitle(t)), 'gi_renewals.tasks_done', { count: plans.length });
  return (
    <div data-testid="gi-renewals">
      <div className="callout small row spread wrap" style={{ gap: 8, marginBottom: 12 }}>
        <span>{t('gi_renewals.intro', { days: giRenewalDays(view).join(' / ') })}</span>
        <button className="btn btn-sm" disabled={!plans.length} onClick={createTasks} data-testid="gi-create-tasks">{t('gi_renewals.create_tasks', { count: plans.length })}</button>
      </div>
      {items.length === 0 ? <Empty icon="check">{t('gi_renewals.empty')}</Empty> : <div className="board" style={{ gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(200px, 1fr))` }}>
        {COLUMNS.map((c) => { const list = items.filter((x) => col(x) === c); return (
          <section key={c} className="column" data-testid={`gi-stage-${c}`}>
            <div className="column-head"><b>{t(`gi_renewals.stage.${c}`)}</b><span className="tiny muted num">{list.length}</span></div>
            {list.map((it) => <GiCard key={`${it.policy.id}:${it.dueDate}`} it={it} />)}
          </section>); })}
      </div>}
      <p className="tiny muted">{t('gi_renewals.note')}</p>
    </div>
  );
  function GiCard({ it }: { it: GiRenewalItem }) {
    const idx = it.stage ? GI_OPEN_STAGES.indexOf(it.stage as typeof GI_OPEN_STAGES[number]) : -1;
    const next: GiRenewalStage = idx + 1 < GI_OPEN_STAGES.length ? GI_OPEN_STAGES[idx + 1]! : 'renewed';
    return (
      <article className="case-card" data-testid="gi-card">
        <PersonCell id={it.personId} sub={`${it.policy.productName}${it.policy.carrier ? ' · ' + it.policy.carrier : ''}`} />
        <div className="row wrap" style={{ gap: 4 }}>
          <span className={`chip ${it.days < 0 ? 'crit' : it.days <= 15 ? 'high' : 'gold'}`}>{it.days < 0 ? t('gi_renewals.expired', { date: i18n.date(it.dueDate, 'dayMonth') }) : t('gi_renewals.in_days', { days: it.days, date: i18n.date(it.dueDate, 'dayMonth') })}</span>
          {it.renewalPremium != null && <span className="chip">{i18n.money(it.renewalPremium, it.currency)}</span>}
        </div>
        <div className="row wrap" style={{ gap: 4 }}>
          <button className="btn btn-sm" onClick={() => rt.open(<GiStageForm it={it} stage={next} />)} data-testid="gi-advance">{t(`gi_renewals.stage.${next}`)}<Icon name="arrow" size={12} /></button>
          <button className="btn btn-sm btn-ghost" onClick={() => rt.open(<GiStageForm it={it} stage="lost" />)} data-testid="gi-lost">{t('gi_renewals.stage.lost')}</button>
        </div>
      </article>
    );
  }
}

function GiStageForm({ it, stage: initial }: { it: GiRenewalItem; stage: GiRenewalStage }) {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const [stage, setStage] = useState<GiRenewalStage>(initial);
  const [prem, setPrem] = useState<number | null>(it.renewalPremium);
  const [notes, setNotes] = useState(it.renewal?.notes ?? '');
  const save = async () => { if (await rt.run((v, c) => moveGeneralRenewal(v, { policyId: it.policy.id, dueDate: it.dueDate, stage, renewalPremium: prem, notes }, c), `gi_renewals.moved.${stage}`)) rt.close(); };
  return (
    <Modal title={`${it.policy.productName} · ${i18n.date(it.dueDate)}`} footer={<><button className="btn" onClick={rt.close}>{t('common.cancel')}</button><button className="btn btn-primary" disabled={stage === it.stage} onClick={() => void save()} data-testid="gi-save">{t('common.save')}</button></>}>
      <div className="form-grid">
        <Field label={t('gi_renewals.stage_label')}><select className="select" value={stage} onChange={(e: SelEv) => setStage(e.target.value as GiRenewalStage)} data-testid="gi-stage">{GI_RENEWAL_STAGES.map((s) => <option key={s} value={s}>{t(`gi_renewals.stage.${s}`)}</option>)}</select></Field>
        <Field label={t('renewals.new')}><NumInput value={prem} onChange={setPrem} testId="gi-premium" /></Field>
        <Field label={t('common.notes')} span2><input className="input" value={notes} onChange={(e: Ev) => setNotes(e.target.value)} /></Field>
      </div>
      {(stage === 'renewed' || stage === 'lost') && <p className="tiny muted" style={{ marginBottom: 0 }}>{t(stage === 'renewed' ? 'renewals.note' : 'gi_renewals.lost_note')}</p>}
    </Modal>
  );
}
